// What the address a payer opened identifies. Decided from the address alone,
// before any request, because the answer picks WHICH request is made.
export type PortalEntry =
  // The bare front door or a static route: nothing to look up.
  | { kind: "none"; homePath: string }
  | { kind: "slug"; slug: string; homePath: string }
  | { kind: "paylink"; paylink: string; homePath: string }
  // An address that can only be a payment link and is not a usable one.
  | { kind: "invalid"; homePath: string };

// The first segment of a prefixed payment link address. Reserved, so a page
// can never be looked up under this name.
const PAYLINK_SEGMENT = "paylink";

// Static routes in their own right. `/:corporateName` is greedy, so without
// this list a verify link would be resolved as a school called "verify".
const STATIC_SEGMENTS = ["verify"];

const LINK_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isPayLinkId = (value: string): boolean => LINK_ID.test(value);

// The link id is looked up in lower case, the form it is minted in, so one
// link typed in two cases is one lookup and one cached answer. The home
// address keeps the case the payer arrived with.
export const entryFromPath = (pathname: string): PortalEntry => {
  const [first, second] = pathname.split("/").filter(Boolean);

  if (!first || STATIC_SEGMENTS.includes(first)) {
    return { kind: "none", homePath: "/" };
  }

  if (first === PAYLINK_SEGMENT) {
    return second && isPayLinkId(second)
      ? {
          kind: "paylink",
          paylink: second.toLowerCase(),
          homePath: `/${PAYLINK_SEGMENT}/${second}`,
        }
      : { kind: "invalid", homePath: "/" };
  }

  // Links handed out before the prefixed address existed put the link id
  // first. The shortener's stored redirects point at that form and are never
  // rewritten, so it is accepted for as long as those links circulate.
  if (isPayLinkId(first)) {
    return {
      kind: "paylink",
      paylink: first.toLowerCase(),
      homePath: `/${first}`,
    };
  }

  return { kind: "slug", slug: first, homePath: `/${first}` };
};
